import { useState, useEffect } from "react";
import { User, X, Settings, LogOut, Bell, Calendar, Mail, Phone, Building2, Shield, ChevronRight, Clock, Target } from "lucide-react";
import axios from "axios";
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

export const MonEspaceButton = ({ user, onLogout, onOpenCalendrier, onOpenNotifications, onOpenSettings, compact = false, className = "" }) => {
  const [showPanel, setShowPanel] = useState(false);
  const [stats, setStats] = useState({ leadsTraites: 0, rdvPris: 0, appelsEffectues: 0 });
  const [loading, setLoading] = useState(false);
  
  // Obtenir les initiales de l'utilisateur
  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };
  
  const userName = user?.name || user?.email?.split("@")[0] || "Utilisateur";
  const userEmail = user?.email || "";
  const userRole = user?.role || "collaborateur";
  const initials = getInitials(userName);
  
  // Charger les stats quand le panel s'ouvre
  useEffect(() => {
    if (showPanel && userEmail) {
      loadUserStats();
    }
  }, [showPanel, userEmail]);
  
  const loadUserStats = async () => {
    setLoading(true);
    try {
      // Essayer de charger les stats utilisateur
      const response = await axios.get(`${API}/telepro/stats/user/${encodeURIComponent(userEmail)}`);
      if (response.data) {
        setStats({
          leadsTraites: response.data.leads_traites || 0,
          rdvPris: response.data.rdv_pris || 0,
          appelsEffectues: response.data.appels || 0
        });
      }
    } catch (err) {
      // Si l'API n'existe pas, garder les valeurs par défaut
      console.log("Stats utilisateur non disponibles");
    }
    setLoading(false);
  };
  
  const handleMenuClick = (action, label) => {
    setShowPanel(false);
    if (action) {
      action();
    } else {
      // Toast ou message si pas d'action définie
      console.log(`${label} - fonctionnalité à venir`);
    }
  };
  
  // Formater le rôle pour l'affichage
  const formatRole = (role) => {
    const roles = {
      'admin': 'Administrateur',
      'telepro': 'Téléprospecteur',
      'technicien': 'Technicien',
      'commercial': 'Commercial',
      'comptable': 'Comptable',
      'collaborateur': 'Collaborateur'
    };
    return roles[role] || role;
  };
  
  // Couleur du badge selon le rôle
  const getRoleBadgeColor = (role) => {
    const colors = {
      'admin': 'bg-amber-500/20 text-amber-300',
      'telepro': 'bg-blue-500/20 text-blue-300',
      'technicien': 'bg-emerald-500/20 text-emerald-300',
      'commercial': 'bg-purple-500/20 text-purple-300',
      'comptable': 'bg-cyan-500/20 text-cyan-300'
    };
    return colors[role] || 'bg-indigo-500/20 text-indigo-300';
  };
  
  return (
    <>
      <button
        onClick={() => setShowPanel(true)}
        className={`flex items-center gap-2 ${compact ? 'p-1.5' : 'px-3 py-2'} bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-all group ${className}`}
        data-testid="mon-espace-btn"
        title="Mon Profil"
      >
        {/* Avatar avec initiales */}
        <div className={`${compact ? 'w-9 h-9' : 'w-8 h-8'} rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-sm font-semibold shadow-lg group-hover:shadow-indigo-500/30 transition-shadow`}>
          {initials}
        </div>
        {!compact && <span className="hidden sm:inline text-sm font-medium">Mon Espace</span>}
      </button>
      
      {showPanel && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex justify-end" onClick={() => setShowPanel(false)}>
          <div 
            className="w-full max-w-sm bg-slate-900 border-l border-slate-700 h-full overflow-y-auto animate-slide-in-right"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header du panneau */}
            <div className="sticky top-0 bg-slate-900 border-b border-slate-700 px-4 py-4 flex justify-between items-center z-10">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <User className="w-5 h-5 text-indigo-400" />
                Mon Espace
              </h2>
              <button
                onClick={() => setShowPanel(false)}
                className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
                data-testid="close-mon-espace"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Profil utilisateur - Amélioré */}
            <div className="p-6 border-b border-slate-800 bg-gradient-to-br from-slate-800/50 to-slate-900">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-2xl font-bold shadow-lg shadow-indigo-500/20 flex-shrink-0">
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold text-lg truncate">{userName}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Mail className="w-3.5 h-3.5 text-slate-500" />
                    <p className="text-slate-400 text-sm truncate">{userEmail}</p>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${getRoleBadgeColor(userRole)}`}>
                      {userRole === 'admin' && <Shield className="w-3 h-3" />}
                      {formatRole(userRole)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Informations supplémentaires */}
              {user?.dashboard_access && (
                <div className="mt-4 pt-4 border-t border-slate-700/50">
                  <p className="text-xs text-slate-500 mb-2">Accès dashboards:</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(user.dashboard_access)
                      .filter(([_, v]) => v === true)
                      .slice(0, 5)
                      .map(([key]) => (
                        <span key={key} className="px-2 py-0.5 bg-slate-700/50 text-slate-400 text-xs rounded">
                          {key.replace(/_/g, ' ')}
                        </span>
                      ))
                    }
                    {Object.values(user.dashboard_access).filter(v => v === true).length > 5 && (
                      <span className="px-2 py-0.5 bg-slate-700/50 text-slate-400 text-xs rounded">
                        +{Object.values(user.dashboard_access).filter(v => v === true).length - 5}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Menu rapide - Avec actions */}
            <div className="p-4 space-y-1">
              <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-3 px-2">Menu Rapide</h4>
              
              <button 
                onClick={() => handleMenuClick(onOpenNotifications, "Notifications")}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-slate-800 text-slate-300 hover:text-white transition-colors text-left group"
              >
                <Bell className="w-5 h-5 text-amber-400" />
                <span className="flex-1">Mes Notifications</span>
                <span className="bg-amber-500/20 text-amber-300 text-xs px-2 py-0.5 rounded-full">
                  Bientôt
                </span>
                <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-slate-400" />
              </button>
              
              <button 
                onClick={() => handleMenuClick(onOpenCalendrier, "Mon Planning")}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-slate-800 text-slate-300 hover:text-white transition-colors text-left group"
              >
                <Calendar className="w-5 h-5 text-teal-400" />
                <span className="flex-1">Mon Planning</span>
                <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-slate-400" />
              </button>
              
              <button 
                onClick={() => handleMenuClick(onOpenSettings, "Paramètres")}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-slate-800 text-slate-300 hover:text-white transition-colors text-left group"
              >
                <Settings className="w-5 h-5 text-slate-400" />
                <span className="flex-1">Paramètres</span>
                <span className="bg-slate-700 text-slate-400 text-xs px-2 py-0.5 rounded-full">
                  Bientôt
                </span>
                <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-slate-400" />
              </button>
            </div>
            
            {/* Stats personnelles - Dynamiques */}
            <div className="p-4 border-t border-slate-800">
              <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-3 px-2 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" />
                Cette semaine
              </h4>
              {loading ? (
                <div className="text-center py-4 text-slate-500 text-sm">Chargement...</div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-emerald-400">{stats.leadsTraites}</div>
                    <div className="text-[10px] text-slate-400">Leads</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-blue-400">{stats.rdvPris}</div>
                    <div className="text-[10px] text-slate-400">RDV</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-purple-400">{stats.appelsEffectues}</div>
                    <div className="text-[10px] text-slate-400">Appels</div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Informations de session */}
            <div className="p-4 border-t border-slate-800">
              <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-3 px-2">Session</h4>
              <div className="bg-slate-800/30 rounded-lg p-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Connecté depuis</span>
                  <span className="text-slate-300">Aujourd'hui</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Dernière activité</span>
                  <span className="text-slate-300">À l'instant</span>
                </div>
              </div>
            </div>
            
            {/* Déconnexion */}
            <div className="p-4 border-t border-slate-800">
              <button 
                onClick={() => {
                  setShowPanel(false);
                  if (onLogout) onLogout();
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                data-testid="logout-btn-panel"
              >
                <LogOut className="w-5 h-5" />
                <span>Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MonEspaceButton;
